import axios, { InternalAxiosRequestConfig } from 'axios';
import { API_URL } from '../constants/api';
import { universalAuthService } from './universalAuthService';

// Вспомогательная функция для декодирования JWT токена
const decodeTokenPayload = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return {};
  }
};

// Типы данных
interface AuthRequest {
  email: string;
  password: string;
}

interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  role: string;
  phone: string;
}

interface AuthResponse {
  token: string;
  refreshToken?: string;
  user: any; // Разные роли возвращают разные данные (customer, field, referee)
  emailVerified?: boolean;
}

interface RegistrationResponse {
  success: boolean;
  message: string;
  email: string;
}

// Регистрация нового пользователя
export const registerUser = async (name: string, email: string, password: string, phone: string): Promise<RegistrationResponse> => {
  const request = {
    name,
    email,
    password,
    phone
  };
  
  const response = await axios.post(`${API_URL}/api/auth/register`, request);
  
  // Унифицированное API возвращает { message: string }
  return {
    success: true,
    message: response.data.message || 'Registration successful',
    email: email
  };
};

// Авторизация пользователя
// Возвращает сырые данные от бэкенда - AuthContext сам решит куда сохранить
export const loginUser = async (email: string, password: string): Promise<AuthResponse> => {
  const request = {
    email,
    password
  };

  const response = await axios.post(`${API_URL}/api/auth/login`, request);

  // Унифицированное API возвращает { token, refreshToken, user, message }
  // user содержит разные поля в зависимости от роли:
  // - customer: ranks, gameStatistics, achievements, clans...
  // - field: address, supportedGameTypes, rating...
  // - referee: isMainReferee, fieldAssignments, gameTypes...
  const token = response.data.token;
  const refreshToken = response.data.refreshToken;
  const user = response.data.user;

  // НЕ трансформируем данные - возвращаем как есть от бэкенда
  // AuthContext.tsx определит роль и сохранит в правильный storage (user/field/referee)
  return {
    token: token,
    refreshToken: refreshToken,
    user: user, // Сырые данные - isMainReferee, fieldAssignments будут здесь для referee!
    emailVerified: user.isEmailVerified || user.emailVerified || false
  };
};

// Функция для обновления токенов
// userType параметр позволяет обновлять токены для разных типов пользователей (user, field, referee)
export const refreshAccessToken = async (userType: 'user' | 'field' | 'referee' = 'user'): Promise<{ token: string; refreshToken: string } | null> => {
  try {
    // Получаем токены для конкретного типа пользователя
    const { token, refreshToken } = universalAuthService.getStoredData(userType);
    if (!token || !refreshToken) {
      console.log(`[${userType}] No token or refresh token available`);
      return null;
    }

    const request = {
      token,
      refreshToken
    };

    const response = await axios.post(`${API_URL}/api/auth/refresh-token`, request);

    if (response.data && response.data.token && response.data.refreshToken) {
      return {
        token: response.data.token,
        refreshToken: response.data.refreshToken
      };
    }

    return null;
  } catch (error) {
    console.error(`[${userType}] Token refresh failed:`, error);
    return null;
  }
};

// Внутренняя функция для сохранения данных (не экспортируется)
const saveAuthData = (data: AuthResponse): void => {
  // Обновляем объект пользователя, добавляя emailVerified, если он есть в ответе
  const user = {
    ...data.user,
    emailVerified: data.emailVerified !== undefined ? data.emailVerified : data.user.emailVerified
  };
  
  // Используем универсальный сервис для основных данных
  universalAuthService.saveData('user', user, data.token, data.refreshToken);
};

// Получение данных из localStorage
export const getAuthData = (): { token: string | null; refreshToken: string | null; user: any } => {
  const { token, userData, refreshToken } = universalAuthService.getStoredData('user');
  
  return { token, refreshToken, user: userData };
};

// Внутренняя функция для очистки данных (не экспортируется)
const clearAuthData = (): void => {
  universalAuthService.clearData('user');
};

// Простая функция для выполнения выхода
export const performLogout = (): void => {
  // Очищаем данные авторизации
  clearAuthData();

  // Форсированное завершение текущих запросов
  if (window.stop) {
    window.stop();
  }

  // Принудительная перезагрузка страницы с перенаправлением на главную страницу
  window.location.href = '/';
};

// Внутренняя функция для мягкого выхода (не экспортируется)
const softLogout = (): void => {
  // Очищаем данные авторизации
  clearAuthData();
  
  // Перенаправляем на главную без перезагрузки
  window.location.href = '/';
};

// Хранилище для текущих interceptors
let currentRequestInterceptor: number | null = null;
let currentResponseInterceptor: number | null = null;

// Настройка axios для использования токена в запросах
export const setupAxiosInterceptors = (token: string): void => {
  // Удаляем предыдущий interceptor если он есть
  if (currentRequestInterceptor !== null) {
    axios.interceptors.request.eject(currentRequestInterceptor);
  }
  
  // Добавляем новый request interceptor
  currentRequestInterceptor = axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  // Добавляем response interceptor для обработки ошибок авторизации
  currentResponseInterceptor = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        
        console.log('401 error detected, attempting token refresh...');
        
        // Попытка обновить токен
        const newTokens = await refreshAccessToken();
        if (newTokens) {
          console.log('Token refresh successful, retrying request...');
          
          // Обновляем токены в localStorage
          universalAuthService.updateTokens('user', newTokens.token, newTokens.refreshToken);
          
          // Обновляем заголовок Authorization в оригинальном запросе
          originalRequest.headers.Authorization = `Bearer ${newTokens.token}`;
          
          // Повторяем оригинальный запрос
          return axios(originalRequest);
        } else {
          console.log('Token refresh failed, performing logout...');
          // Если обновление не удалось - делаем logout
          softLogout();
        }
      }
      
      return Promise.reject(error);
    }
  );
};

// Повторная отправка верификационного email
export const resendVerificationEmail = async (email: string): Promise<boolean> => {
  try {
    // Пока используем старый endpoint, так как унифицированное API не поддерживает повторную отправку
    const response = await axios.post(`${API_URL}/api/EmailVerification/resend`, { email });
    return response.status === 200;
  } catch (error) {
    console.error('Error resending verification email:', error);
    return false;
  }
};

// Проверка валидности текущего токена
export const validateToken = async (): Promise<boolean> => {
  return await universalAuthService.validateToken('user');
}; 